import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ModalBase from "../../../../../Pages/utils/ModalBase";
import * as yup from "yup";
import { useAuth } from "../../../../../../context/auth";
import { actionShowSpinner } from "../../../../../../redux-flow/action/Wrapper/Spinner";
import {
  create,
  getById,
  toggleActive,
  update,
} from "../../../../../../services/business.service";
import { getMembersByName } from "../../../../../../services/formCombos.service";
import AddButton from "../../../../../Common/AddButton";
import {
  ColorInput,
  Container as ColorPickerContainer,
  ColorValue,
  Label,
  ValueContainer,
} from "../../../../../Common/ColorPicker/styles";
import DialogCustom from "../../../../../Common/DialogCustom";
import ToastMessage from "../../../../../Common/ToastMessage";
import CommitteePackagesBox from "../../../components/BoxLists/CommitteePackagesList";
import Header from "../../../components/Header";
import {
  BUInfoContainer,
  BuRelationContainer,
  BURelationsContainer,
  BuInfoInputsContainer,
  Container,
  FormContainer,
  SaveBuButtonContainer,
  SectionTitle,
  Title,
} from "./styles";
import CreateCompanyModal from "../../../Modals/CreateCompany";
import CompaniesList from "../../../components/BoxLists/CompaniesList";
import { ICompany } from "../../../../../../types/companies";
import { updateCompanyStatus } from "../../../../../../services/companies.service";

const useStyles = makeStyles((theme) => ({
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
  },
}));

interface IBusinessData {
  name: string;
  color: string;
  adminName: string;
  adminId: string;
}

const schema = yup.object().shape({
  adminName: yup.string().required(),
  adminId: yup.string().required(),
  name: yup.string().required(),
  color: yup.string().required(),
});

const BusinessUnitConfiguration: React.FC = ({ actionShowSpinner }: any) => {
  const location = useLocation<{ id?: string }>();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<IBusinessData>({
    resolver: yupResolver(schema),
  });
  const { userInfo } = useAuth() as any;

  const [toastMessage, setToastMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [committeePackages, setCommitteePackages] = useState([]);
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [companyToEdit, setCompanyToEdit] = useState<ICompany | undefined>();
  const [createCompanyModalOpened, setCreateCompanyModalOpened] =
    useState(false);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [adminNameToSearch, setadminNameToSearch] = useState<string>("");
  const [participantOptions, setParticipantOptions] = useState<
    { id: string; name: string }[]
  >([]);
  const [businessActive, setBusinessActive] = useState(false);
  const [disableBusinessDialogOpened, setDisableBusinessDialogOpened] =
    useState(false);

  useEffect(() => {
    if (businessId) {
      fetchBusinessById(businessId);
    }
  }, [businessId]);

  useEffect(() => {
    if (userInfo?.master === false) {
      history.push("/");
    }
  }, [i18n.language, userInfo]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");

    if (id) {
      setBusinessId(id || null);
    }
  }, [location]);

  useEffect(() => {
    if (adminNameToSearch?.length >= 3) {
      const delayDebounceFn = setTimeout(() => {
        getMembersByName("pt", adminNameToSearch).then((res) => {
          if (res.error?.length > 0 || !res?.data) {
            setParticipantOptions([]);

            return;
          }

          const responseData = res.data as [];
          setParticipantOptions(responseData);
        });
      }, 1000);

      return () => clearTimeout(delayDebounceFn);
    } else {
      setParticipantOptions([]);
    }
  }, [adminNameToSearch]);

  const fetchBusinessById = useCallback(async (id: string) => {
    actionShowSpinner(true);

    try {
      const business = await getById(id);

      setParticipantOptions([
        {
          id: business.adminMemberId,
          name: business.adminMemberName,
        },
      ]);
      setBusinessActive(business.active);

      reset({
        color: business.color,
        adminId: business.adminMemberId,
        adminName: business.adminMemberName,
        name: business.name,
      });

      setCommitteePackages(business.committeePackages);
      setCompanies(business.companies);
    } catch (err: any) {
      const [errorMessage] = err?.response?.data?.errors || [
        t("softwareManagement.modules.business.errorFetchCommitteePackages"),
      ];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });
    }

    actionShowSpinner(false);
  }, []);

  const onSubmit = useCallback(
    async (data: IBusinessData) => {
      actionShowSpinner(true);

      if (businessId) {
        await updateBusiness(data);
      } else {
        await createBusiness(data);
      }

      actionShowSpinner(false);
    },
    [errors, businessId]
  );

  const createBusiness = useCallback(async (data: IBusinessData) => {
    try {
      const createdBusinessId = await create({
        name: data.name,
        color: data.color,
        adminMemberId: data.adminId,
      });

      setBusinessId(createdBusinessId);

      setToastMessage({
        open: true,
        content: t("softwareManagement.modules.business.successCreation"),
        type: "success",
        autoHideDuration: 4000,
      });
    } catch (err: any) {
      const [errorMessage] = err?.response?.data?.errors || [
        t("softwareManagement.modules.business.errorCreation"),
      ];

      setToastMessage({
        autoHideDuration: 4000,
        content: errorMessage,
        open: true,
        type: "error",
      });
    }
  }, []);

  const updateBusiness = useCallback(
    async (data: IBusinessData) => {
      if (!businessId) return;

      try {
        await update({
          name: data.name,
          color: data.color,
          adminMemberId: data.adminId,
          id: businessId,
        });

        setToastMessage({
          open: true,
          content: t("softwareManagement.modules.business.successUpdate"),
          type: "success",
          autoHideDuration: 4000,
        });
      } catch (err: any) {
        const [errorMessage] = err?.response?.data?.errors || [
          t("softwareManagement.modules.business.errorUpdate"),
        ];

        setToastMessage({
          autoHideDuration: 4000,
          content:
            errorMessage ||
            t("softwareManagement.modules.business.errorUpdate"),
          open: true,
          type: "error",
        });
      }
    },
    [businessId]
  );

  const handleClickOpen = (_event: any) => {
    setDisableBusinessDialogOpened(true);
  };

  const handleToggleActive = useCallback(async () => {
    if (!businessId) return;

    actionShowSpinner(true);

    try {
      await toggleActive(businessId, !businessActive);

      setBusinessActive(!businessActive);
    } catch (err: any) {
      const [errorMessage] =
        err?.response?.data?.errors ||
        t("softwareManagement.modules.groups.errorCreation");

      setToastMessage({
        autoHideDuration: 4000,
        content:
          errorMessage ||
          t("softwareManagement.modules.business.errorToggleActive"),
        open: true,
        type: "error",
      });
    }

    setDisableBusinessDialogOpened(false);
    actionShowSpinner(false);
  }, [businessId, businessActive]);

  const handleUpdateCompany = useCallback((company: ICompany) => {
    setCreateCompanyModalOpened(true);
    setCompanyToEdit(company);
  }, []);

  const handleUpdateCompanyStatus = async (
    id: string,
    data: { isActivated: boolean }
  ): Promise<void> => {
    actionShowSpinner(true);
    try {
      const updatedCompany = await updateCompanyStatus({ data, id });

      const newCompanyArray = companies.map((company) => {
        if (company.id == updatedCompany.id) {
          return {...company, activated: updatedCompany.activated };
        }
        return company;
      })

      setCompanies(newCompanyArray);

    } catch (err: any) {
      const [errorMessage] = err?.response?.data?.errors || [
        t("softwareManagement.modules.business.errorUpdateCompanyStatus"),
      ];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });

      throw new Error()
    } finally {
      actionShowSpinner(false);
    }
  };

  return (
    <Container>
      <Header
        title={t("softwareManagement.modules.business.settings")}
      ></Header>

      <FormContainer>
        <BUInfoContainer elevation={1}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <SectionTitle>
              <Title>
                {t("softwareManagement.modules.business.infomations")}
              </Title>

              {businessId && (
                <FormControlLabel
                  control={
                    <Tooltip
                      title={businessActive ? t("deactivate") : t("activate")}
                    >
                      <Switch
                        checked={businessActive}
                        onChange={handleClickOpen}
                        color="primary"
                      />
                    </Tooltip>
                  }
                  label={businessActive ? t("active") : t("inactive")}
                />
              )}
            </SectionTitle>

            <BuInfoInputsContainer container spacing={2}>
              <Grid item lg={6} xs={12}>
                <TextField
                  autoComplete="nope"
                  id="name"
                  label={t("softwareManagement.name")}
                  fullWidth
                  required={true}
                  error={!watch("name")}
                  {...register("name")}
                  InputLabelProps={{ shrink: watch("name") ? true : false }}
                />
              </Grid>

              <Grid item lg={6} xs={12}>
                <Autocomplete
                  options={participantOptions}
                  getOptionLabel={(option) => option.name || ""}
                  onChange={(_event, newValue) => {
                    setValue("adminId", newValue?.id || "");
                  }}
                  value={
                    watch("adminId")
                      ? participantOptions.find(
                          (participant) => participant.id === watch("adminId")
                        )
                      : null
                  }
                  onInputChange={(_event, newInputValue, prop) => {
                    setadminNameToSearch(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("softwareManagement.modules.business.admin")}
                      required
                      error={!watch("adminName")}
                      value={watch("adminName")}
                      {...register("adminName")}
                    />
                  )}
                />
              </Grid>

              <Grid item lg={6} xs={12}>
                <ColorPickerContainer>
                  <Label>Color Picker</Label>

                  <ValueContainer>
                    <ColorInput
                      type="color"
                      {...register("color")}
                      value={watch("color")}
                      defaultValue="#465eb6"
                    />
                    <ColorValue>{watch("color") || "#4654b6"}</ColorValue>
                  </ValueContainer>
                </ColorPickerContainer>
              </Grid>
            </BuInfoInputsContainer>

            <SaveBuButtonContainer>
              <Button
                variant="contained"
                className={classes.button}
                type="submit"
              >
                Salvar
              </Button>
            </SaveBuButtonContainer>
          </form>
        </BUInfoContainer>

        {businessId && (
          <BURelationsContainer>
            <BuRelationContainer elevation={1}>
              <SectionTitle>
                <Title>
                  {t("softwareManagement.modules.business.buPackages")}
                </Title>

                <AddButton
                  onClick={() =>
                    history.push(
                      "/system-management/configure-committee-packages"
                    )
                  }
                />
              </SectionTitle>

              <CommitteePackagesBox packages={committeePackages} />
            </BuRelationContainer>

            <BuRelationContainer elevation={1}>
              <SectionTitle>
                <Title>
                  {t("softwareManagement.modules.business.buCompanies")}
                </Title>

                <AddButton onClick={() => setCreateCompanyModalOpened(true)} />
              </SectionTitle>

              <CompaniesList
                companies={companies}
                handleUpdateCompany={handleUpdateCompany}
                handleUpdateCompanyStatus={handleUpdateCompanyStatus}
              />
            </BuRelationContainer>
          </BURelationsContainer>
        )}
      </FormContainer>

      <ToastMessage
        message={toastMessage.content}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />

      <DialogCustom
        open={disableBusinessDialogOpened}
        handleClose={() => {
          setDisableBusinessDialogOpened(false);
        }}
        handleChange={() => handleToggleActive()}
        title={t(`forms.dialog.alert`)}
        body={
          businessActive
            ? t("softwareManagement.modules.business.textInactivate")
            : t("softwareManagement.modules.business.textActivate")
        }
        confirmText={t(`forms.dialog.yes`)}
        cancelText={t(`forms.dialog.no`)}
        alert={true}
      />

      <ModalBase
        open={createCompanyModalOpened}
        setOpen={setCreateCompanyModalOpened}
        content={
          <CreateCompanyModal
            onSuccess={() => fetchBusinessById(businessId || "")}
            setModalStatus={(value) => setCreateCompanyModalOpened(value)}
            businessId={businessId || ""}
            setToastMessage={setToastMessage}
            companyToEdit={companyToEdit}
          />
        }
      />
    </Container>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  actionShowSpinner: (request: any) => dispatch(actionShowSpinner(request)),
});

const BusinessUnitConfigurationConnection = connect(
  null,
  mapDispatchToProps
)(BusinessUnitConfiguration);

export default BusinessUnitConfigurationConnection;
